import { Grid } from '@design-systems/canvas-ui'
import { ErrorAlert } from 'components/error-fallback'
import {
    GeneralizedComponent,
    GeneralizedComponentPayloadType,
} from 'components/generalized-component/generalized-component'
import { BasicPlaceholder } from 'components/placeholder'
import {
    useGetTheftStoreInternal,
    useGetTheftStoreExternal,
} from 'hooks/assets-protection/useTheftStore'
import { useEssentials } from 'hooks/utils/useEssentials'
import { translateTableData } from 'locales/utils'
import { Cell, TableData } from 'utils/data'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { Container } from 'components/container/Container'
import { useCallback, useMemo } from 'react'
import { buildTheftStoreTransposedTableData } from '../helpers'
import {
    EXTERNAL_HEADINGS,
    EXTERNAL_METRICS_DOLLARS,
    EXTERNAL_METRICS_PERCENT,
    INTERNAL_HEADINGS,
    INTERNAL_METRICS_DOLLARS,
    INTERNAL_METRICS_PERCENT,
    MetricConfig,
} from './theft-store.constants'

const hasData = (data: TableData | undefined): boolean =>
    !!data?.rows && data.rows.length > 0

const TheftSection = ({
    isLoading,
    isError,
    error,
    data,
    title,
    payload,
    widths,
    kpiName,
    onRetry,
}: {
    isLoading: boolean
    isError: boolean
    error: unknown
    data: TableData | undefined
    title: string
    payload: GeneralizedComponentPayloadType | undefined
    widths: number[]
    kpiName: string
    onRetry: () => void
}) => {
    if (isLoading) {
        return (
            <div
                className="placeholder-container"
                data-testid={`${kpiName}-placeholder`}
            >
                <BasicPlaceholder type="table" rows={5} columns={5} />
            </div>
        )
    }

    if (isError) {
        return (
            <div className="generalized-component">
                <Grid.Container align="center" justify="space-between">
                    <GeneralizedComponent.Title title={title} />
                </Grid.Container>
                <ErrorAlert error={error} onRetry={onRetry} />
            </div>
        )
    }

    if (!hasData(data)) {
        return (
            <div className="generalized-component">
                <Grid.Container align="center" justify="space-between">
                    <GeneralizedComponent.Title title={title} />
                </Grid.Container>
                <ErrorAlert variant="no-data" onRetry={onRetry} />
            </div>
        )
    }

    return (
        <GeneralizedComponent
            payload={payload ?? {}}
            title={title}
            isLoading={false}
            scrollableWidth={false}
            widths={widths}
            kpiName={kpiName}
        />
    )
}

export const TheftStore = () => {
    const { t, currentLocation, env, session } = useEssentials()
    const { theft } = TRANSLATION_KEYS.assetsProtection
    const { viewInDollars, viewInPercent } =
        TRANSLATION_KEYS.assetsProtection.filters

    const {
        data: internalData,
        isLoading: internalLoading,
        isError: internalError,
        error: internalErr,
        refetch: internalRefetch,
    } = useGetTheftStoreInternal({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
    })

    const {
        data: externalData,
        isLoading: externalLoading,
        isError: externalError,
        error: externalErr,
        refetch: externalRefetch,
    } = useGetTheftStoreExternal({
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
    })

    const isLoading = internalLoading || externalLoading

    const buildPayload = useCallback(
        (
            table: TableData,
            percentConfigs: MetricConfig[],
            dollarConfigs: MetricConfig[],
            headings: Cell[],
        ) => ({
            [t(viewInPercent)]: [
                {
                    title: '',
                    subtitle: '',
                    data: {
                        tab: translateTableData({
                            t,
                            table: buildTheftStoreTransposedTableData(
                                table,
                                percentConfigs,
                                headings,
                            ),
                            translation: theft,
                        }),
                    },
                },
            ],
            [t(viewInDollars)]: [
                {
                    title: '',
                    subtitle: '',
                    data: {
                        tab: translateTableData({
                            t,
                            table: buildTheftStoreTransposedTableData(
                                table,
                                dollarConfigs,
                                headings,
                            ),
                            translation: theft,
                        }),
                    },
                },
            ],
        }),
        [t, theft, viewInPercent, viewInDollars],
    )

    const internalPayload = useMemo(() => {
        if (!hasData(internalData)) {
            return undefined
        }
        const dataWithoutFooter = { ...internalData!, footer: [] }
        return buildPayload(
            dataWithoutFooter,
            INTERNAL_METRICS_PERCENT,
            INTERNAL_METRICS_DOLLARS,
            INTERNAL_HEADINGS,
        )
    }, [internalData, buildPayload])

    const externalPayload = useMemo(
        () =>
            hasData(externalData)
                ? buildPayload(
                      externalData!,
                      EXTERNAL_METRICS_PERCENT,
                      EXTERNAL_METRICS_DOLLARS,
                      EXTERNAL_HEADINGS,
                  )
                : undefined,
        [externalData, buildPayload],
    )

    if (isLoading) {
        return (
            <div className="placeholder-container" data-testid="placeholder">
                <BasicPlaceholder type="table" rows={5} columns={5} />
            </div>
        )
    }

    return (
        <Container className="hc8-pt-4x">
            <Grid.Container>
                <Grid.Item
                    size={{
                        xs: 12,
                    }}
                >
                    <TheftSection
                        isLoading={internalLoading}
                        isError={internalError}
                        error={internalErr}
                        data={internalData}
                        title={t(theft.internal)}
                        payload={internalPayload}
                        widths={[170, 90, 90, 90]}
                        kpiName="assets protection tab theft"
                        onRetry={() => void internalRefetch()}
                    />
                    <TheftSection
                        isLoading={externalLoading}
                        isError={externalError}
                        error={externalErr}
                        data={externalData}
                        title={t(theft.external)}
                        payload={externalPayload}
                        widths={[170, 90, 90]}
                        kpiName="assets protection tab theft"
                        onRetry={() => void externalRefetch()}
                    />
                </Grid.Item>
            </Grid.Container>
        </Container>
    )
}
